import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlSegment } from '@angular/router';
import { POST_LOGIN_ROUTE_KEY } from '@portal/auth';
import { ConfigurationService, OidcSecurityService, OpenIdConfiguration } from 'angular-auth-oidc-client';
import { filter, map, Observable, switchMap, take, tap } from 'rxjs';

const createUrl = (state: UrlSegment[] | RouterStateSnapshot): string => {
  if (state instanceof RouterStateSnapshot) {
    return state.url;
  }
  return state.reduce((acc: string, curr: UrlSegment): string => (acc += `/${curr.toString()}`), '');
};

/**
 * Combined authentication guard that handles:
 * 1. Checking if user is authenticated
 * 2. Redirecting to /realm if no tenant context
 * 3. Triggering OIDC authorize with optional login_hint when tenant context exists
 */
export const authGuard = (
  route: ActivatedRouteSnapshot,
  state: UrlSegment[] | RouterStateSnapshot
): Observable<boolean> => {
  const oidcSecurityService = inject(OidcSecurityService);
  const configurationService = inject(ConfigurationService);
  const router = inject(Router);

  return oidcSecurityService.isAuthenticated$.pipe(
    take(1),
    switchMap(({ isAuthenticated }) => {
      if (isAuthenticated) {
        return [true];
      }

      // Check if we have tenant context from URL (came from /realm redirect)
      const tenantId = route.queryParamMap?.get('tenantId');

      if (!tenantId) {
        // set post login url in storage so we can redirect there after successful login
        const url = router.currentNavigation()?.extractedUrl.toString().substring(1) ?? '';
        localStorage.setItem(POST_LOGIN_ROUTE_KEY, url);
        // No tenant context - redirect to realm selection
        router.navigate(['/realm']);
        return [false];
      }

      // Have tenant context - trigger OIDC authorize
      const usermail = route.queryParamMap?.get('usermail');

      if (!usermail) {
        oidcSecurityService.authorize();
        return [false];
      }

      // Add login_hint for pre-filled email
      return configurationService.getOpenIDConfiguration().pipe(
        filter((config): config is OpenIdConfiguration => config !== null),
        tap(config => {
          config.customParamsAuthRequest = {
            ...(config.customParamsAuthRequest ?? {}),
            login_hint: usermail
          };
        }),
        tap(() => oidcSecurityService.authorize()),
        map(() => false)
      );
    })
  );
};
